import { useState, useEffect } from 'react';

/**
 * Custom hook for handling file uploads
 * @returns {Array} [upload, { loading, error, progress }]
 */
export function useUpload() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const upload = async (file, endpoint = '/upload', additionalData = {}) => {
    setLoading(true);
    setError(null);
    setProgress(0);
    
    // For demo purposes, simulate an upload
    return new Promise((resolve, reject) => {
      let progressValue = 0;
      const interval = setInterval(() => {
        progressValue += 10;
        setProgress(progressValue);
        
        if (progressValue >= 100) {
          clearInterval(interval);
          setLoading(false);
          resolve({ 
            success: true, 
            url: URL.createObjectURL(file),
            filename: file.name
          });
        }
      }, 300);
    });
  };
  
  return [upload, { loading, error, progress }];
}

/**
 * Custom hook for handling streaming responses from AI services
 * @returns {Object} { streamResponse, isStreaming, streamedContent, error, resetStream }
 */
export function useHandleStreamResponse() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const [error, setError] = useState(null);
  
  const streamResponse = async (endpoint, data, streamKey = 'stream') => {
    setIsStreaming(true);
    setStreamedContent('');
    setError(null);
    
    // For demo purposes, simulate a streaming response
    return new Promise((resolve, reject) => {
      const responses = {
        '/integrations/chat-gpt/conversationgpt4': 
          "I'm a simulated AI assistant response. This is a demonstration of the streaming capability in the Infinite AI Project. The actual implementation would connect to real AI services like ChatGPT or Google Gemini. This response is generated locally for demonstration purposes.",
        
        '/integrations/google-gemini-1-5/': 
          "This is a simulated reflection generated by the Infinite AI Project. In a production environment, this would be a thoughtful response from Google's Gemini model based on your prompt. The reflection would be tailored to your specific input and would provide meaningful insights.",
        
        '/api/reflection-generator': 
          "Reflecting on your journey, it's important to acknowledge both the challenges and triumphs that have shaped your path. Each experience contributes to your unique perspective and growth. This simulated reflection demonstrates how the system would provide personalized insights in a production environment."
      };
      
      const responseText = responses[endpoint] || 
        "This is a simulated streaming response from the Infinite AI Project. In a production environment, this would be connected to actual AI services.";
      
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex < responseText.length) {
          const nextChunk = responseText.substring(currentIndex, currentIndex + 5);
          setStreamedContent(prev => prev + nextChunk);
          currentIndex += 5;
        } else {
          clearInterval(interval);
          setIsStreaming(false);
          resolve(streamedContent + responseText);
        }
      }, 50);
    });
  };
  
  const resetStream = () => {
    setStreamedContent('');
    setError(null);
    setIsStreaming(false);
  };
  
  return { 
    streamResponse, 
    isStreaming, 
    streamedContent, 
    error, 
    resetStream 
  };
}

/**
 * Custom hook for managing visual effects
 * @param {Object} initialSettings - Initial visual effect settings
 * @returns {Object} { settings, updateSettings, applyPreset, resetSettings }
 */
export function useVisualEffects(initialSettings = {}) {
  const defaultSettings = {
    particleDensity: 'medium',
    colorScheme: 'nebula',
    animationSpeed: 1,
    effectIntensity: 0.7,
    enableGlow: true,
    enableParticles: true,
    enableAurora: true,
  };
  
  const [settings, setSettings] = useState({
    ...defaultSettings,
    ...initialSettings
  });
  
  const updateSettings = (newSettings) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };
  
  const presets = {
    calm: {
      particleDensity: 'low',
      colorScheme: 'aurora',
      animationSpeed: 0.5,
      effectIntensity: 0.4,
      enableAurora: true,
      enableGlow: true,
      enableParticles: true,
    },
    intense: {
      particleDensity: 'high',
      colorScheme: 'cosmic',
      animationSpeed: 1.5,
      effectIntensity: 1,
      enableAurora: true,
      enableGlow: true,
      enableParticles: true,
    },
    minimal: {
      particleDensity: 'low',
      colorScheme: 'nebula',
      animationSpeed: 0.7,
      effectIntensity: 0.3,
      enableAurora: false,
      enableGlow: true,
      enableParticles: true,
    },
    accessibility: {
      particleDensity: 'low',
      colorScheme: 'nebula',
      animationSpeed: 0.3,
      effectIntensity: 0.2,
      enableAurora: false,
      enableGlow: false,
      enableParticles: false,
    }
  };
  
  const applyPreset = (presetName) => {
    if (presets[presetName]) {
      setSettings(presets[presetName]);
    } else {
      console.warn(`Preset "${presetName}" not found`);
    }
  };
  
  const resetSettings = () => {
    setSettings(defaultSettings);
  };
  
  // Apply CSS variables when settings change
  useEffect(() => {
    document.documentElement.style.setProperty('--particle-density', 
      settings.particleDensity === 'low' ? '50' : 
      settings.particleDensity === 'medium' ? '100' : 
      settings.particleDensity === 'high' ? '200' : '100'
    );
    
    document.documentElement.style.setProperty('--animation-speed', settings.animationSpeed);
    document.documentElement.style.setProperty('--effect-intensity', settings.effectIntensity);
    
    // Toggle classes based on enabled effects
    if (settings.enableGlow) {
      document.body.classList.add('enable-glow');
    } else {
      document.body.classList.remove('enable-glow');
    }
    
    if (settings.enableParticles) {
      document.body.classList.add('enable-particles');
    } else {
      document.body.classList.remove('enable-particles');
    }
    
    if (settings.enableAurora) {
      document.body.classList.add('enable-aurora');
    } else {
      document.body.classList.remove('enable-aurora');
    }
  }, [settings]);
  
  return {
    settings,
    updateSettings,
    applyPreset,
    resetSettings,
    presets
  };
}
